import React, { useMemo } from "react";
import { Drawing, Scene } from "../SpatialConfigurationMode/types";
import { isSceneWithCoordinates } from "../SpatialConfigurationMode/utils";
import { ColorsLegend } from "./ColorsLegend";
import { DrawingTool } from "./DrawingTool";
import {
  ALIAS_STROKE_MM,
  DEFAULT_NODE_OPACITY,
  HIGHLIGHT_STROKE_MM,
  SVG_CIRCLE_SIZE_MM,
  colorPerSceneScanType,
  getNodeColorPerScanType,
} from "./drawing-utils";
import { AliasIcon } from "./shapes/AliasIcon";
import { SimpleCircle } from "./shapes/SimpleCircle";

const SELECTED_CIRCLE_STROKE_COLOR = "#FFBF00";

type DrawingToolContainerProps = {
  scenes: Scene[];
  selectedSceneIds?: string[];
  onDrawingClick?: (x: number, y: number) => void;
  showOriginAndScale?: boolean;
  drawing: Drawing;
  enableDrag?: boolean;
  enableMiniMap?: boolean;
  treeWidth?: number;
};

export const DrawingToolContainer = (props: DrawingToolContainerProps) => {
  const {
    scenes,
    selectedSceneIds,
    onDrawingClick,
    drawing,
    showOriginAndScale,
    enableDrag,
    enableMiniMap,
    treeWidth,
  } = props;

  const nodeIcons = useMemo(
    () =>
      scenes.filter(isSceneWithCoordinates).map((scene) => {
        const isSelected = selectedSceneIds?.includes(scene.id);
        const isAlias = !!scene.aliasSceneId;
        const strokeColor = isSelected
          ? SELECTED_CIRCLE_STROKE_COLOR
          : undefined;
        const strokeWidth = isSelected
          ? HIGHLIGHT_STROKE_MM
          : isAlias
          ? ALIAS_STROKE_MM
          : undefined;
        const node = {
          id: scene.id,
          x: scene.x,
          y: scene.y,
          color: getNodeColorPerScanType(scene.scanType),
          size: SVG_CIRCLE_SIZE_MM,
          strokeColor,
          opacity: isSelected ? 1 : DEFAULT_NODE_OPACITY,
          strokeWidth,
        };

        return isAlias ? (
          <AliasIcon key={node.id} node={node} />
        ) : (
          <SimpleCircle key={node.id} node={node} />
        );
      }),
    [scenes, selectedSceneIds],
  );

  // Bounding box of all placed scene markers, in svg coordinates. Used to skip
  // panning/zooming on selection when every scene already fits on screen.
  const scenesBounds = useMemo(() => {
    const placed = scenes.filter(isSceneWithCoordinates);
    if (placed.length === 0) {
      return undefined;
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const scene of placed) {
      minX = Math.min(minX, scene.x);
      minY = Math.min(minY, scene.y);
      maxX = Math.max(maxX, scene.x);
      maxY = Math.max(maxY, scene.y);
    }
    return { minX, minY, maxX, maxY };
  }, [scenes]);

  // Focus the most recently selected scene: selection ids are appended in the
  // order they were picked, so scan from the end for the latest id that is a
  // scene (the tail may be a non-scene node such as a space/folder).
  const selectedScene = useMemo(() => {
    if (!selectedSceneIds?.length) {
      return undefined;
    }
    for (let i = selectedSceneIds.length - 1; i >= 0; i--) {
      const scene = scenes.find((s) => s.id === selectedSceneIds[i]);
      if (scene) {
        return scene;
      }
    }
    return undefined;
  }, [scenes, selectedSceneIds]);

  return (
    <>
      <DrawingTool
        nodes={nodeIcons}
        onDrawingClick={onDrawingClick}
        drawing={drawing}
        showOriginAndScale={showOriginAndScale}
        enableDrag={enableDrag}
        selectedScene={selectedScene}
        enableMiniMap={enableMiniMap}
        scenesBounds={scenesBounds}
      />
      {!enableMiniMap && (
        <ColorsLegend colors={colorPerSceneScanType} treeWidth={treeWidth} />
      )}
    </>
  );
};
